import { useState, useRef, useEffect } from 'react';
import classes from './PersonalData.module.css';
import accountDetailsClasses from './AccountDetails.module.css';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { updateUserProfile, deleteAccount } from '../../../store/authSlice';
import { SmallModal } from "../../UI/SmallModal/SmallModal";
import SaveNotification from "../../UI/SaveNotification/SaveNotification";
import AvatarEditor from 'react-avatar-editor';
import { parseBirthDate, toHtmlDateInputValue } from '../../../util/birthDateUtils';
import { notifySaveError, notifyValidationToast } from '../../../util/notifyActionError';

const ALLOWED_PROFILE_IMAGE_TYPES = new Set(['image/jpeg', 'image/jpg', 'image/pjpeg', 'image/png', 'image/x-png']);
const ALLOWED_PROFILE_IMAGE_EXT = /\.(jpe?g|png)$/i;

function isAllowedProfileImageFile(file) {
    if (!file) return false;
    const fileType = (file.type || '').toLowerCase();
    if (fileType && ALLOWED_PROFILE_IMAGE_TYPES.has(fileType)) return true;
    return Boolean(file.name && ALLOWED_PROFILE_IMAGE_EXT.test(file.name));
}

const PersonalData = ({ 
        profilePicture, 
        firstName, 
        lastName, 
        email, 
        phoneNumber, 
        birthDate 
    }) => {
    const dispatch = useDispatch();
    const navigate = useNavigate();
    const { id: userId } = useSelector((state) => state?.auth?.user || {});
    const { status, deleteStatus, deleteError } = useSelector(state => state.auth);
    
    // Maintain stable display values to prevent white flash
    const [displayData, setDisplayData] = useState({
        profilePicture: profilePicture,
        firstName: firstName,
        lastName: lastName,
        email: email,
        phoneNumber: phoneNumber,
        birthDate: birthDate
    });
    
    useEffect(() => {
        setDisplayData((prev) => ({
            profilePicture: profilePicture || prev.profilePicture,
            firstName: firstName ?? prev.firstName,
            lastName: lastName ?? prev.lastName,
            email: email != null && email !== '' ? email : prev.email,
            phoneNumber: phoneNumber != null && phoneNumber !== '' ? phoneNumber : prev.phoneNumber,
            birthDate: birthDate != null && birthDate !== '' ? birthDate : prev.birthDate,
        }));
    }, [profilePicture, firstName, lastName, email, phoneNumber, birthDate]);

    // Redirect to homepage after successful deletion
    useEffect(() => {
        if (deleteStatus === "succeeded") {
            navigate('/');
        }
    }, [deleteStatus, navigate]);
    
    const [editingField, setEditingField] = useState(null);
    const [open, setOpen] = useState(false);
    const [modalType, setModalType] = useState('');
    const [previewImage, setPreviewImage] = useState(null);
    const [optimisticProfilePicture, setOptimisticProfilePicture] = useState(null);
    const [validationErrors, setValidationErrors] = useState({});
    const [showSaveNotification, setShowSaveNotification] = useState(false);
    const [saveNotificationMessage, setSaveNotificationMessage] = useState('Änderungen gespeichert');

    const showNotification = (message = 'Änderungen gespeichert') => {
        setSaveNotificationMessage(message);
        setShowSaveNotification(true);
    };
    
    // Delete account state
    const [displayDeleteContent, setDisplayDeleteContent] = useState(false);
    const [deleteModalOpen, setDeleteModalOpen] = useState(false);
    const [deleteModalTab, setDeleteModalTab] = useState(1);
    const [selectedOption, setSelectedOption] = useState('');
    const [textareaContent, setTextareaContent] = useState("");
    const editorRef = useRef(null);
    const editorContainerRef = useRef(null);
    const [scale, setScale] = useState(1);
    const [position, setPosition] = useState({ x: 0.5, y: 0.5 });
    const gestureStateRef = useRef({
        isPanning: false,
        lastPoint: null,
        pinchStartDistance: null,
        startScale: 1,
        startPosition: { x: 0.5, y: 0.5 }
    });
    const [formData, setFormData] = useState({
        firstName: displayData.firstName || '',
        lastName: displayData.lastName || '',
        profileName: displayData.firstName || '',
        birthDate: displayData.birthDate || '',
        email: displayData.email || '',
        phoneNumber: displayData.phoneNumber || '',
        profilePicture: null
    });

    const formatDate = (dateString) => {
        if (!dateString) return '';

        const date = parseBirthDate(dateString);
        if (!date) return dateString;

        return date.toLocaleDateString('de-DE', {
            day: '2-digit',
            month: 'long',
            year: 'numeric'
        });
    };

    const calculateAge = (birthdate) => {
        if (!birthdate) return null;
        
        try {
            const birthDate = parseBirthDate(birthdate);
            if (!birthDate) return null;
            const today = new Date();
            let age = today.getFullYear() - birthDate.getFullYear();
            const monthDiff = today.getMonth() - birthDate.getMonth();

            if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < birthDate.getDate())) {
                age--;
            }

            return age;
        } catch (error) {
            return null;
        }
    };

    const validateField = (name, value) => {
        let error = '';
        
        // Check if value is empty or only whitespace
        if (!value || !value.trim()) {
            error = 'Dieses Feld darf nicht leer sein';
            return error;
        }
        
        switch (name) {
            case 'firstName':
            case 'lastName':
            case 'profileName':
                // Only letters, spaces, hyphens, and apostrophes
                if (!/^[a-zA-ZäöüÄÖÜß\s'-]+$/.test(value)) {
                    error = 'Nur Buchstaben erlaubt';
                }
                break;
            case 'phoneNumber':
                // Only numbers, +, spaces, hyphens, and parentheses
                if (!/^[0-9+\s()-]+$/.test(value)) {
                    error = 'Nur Zahlen und + erlaubt';
                }
                break;
            case 'birthDate':
                // Check age
                const age = calculateAge(value);
                if (age !== null && age < 18) {
                    error = 'Du musst mindestens 18 Jahre alt sein.';
                }
                break;
            default:
                break;
        }
        
        return error;
    };

    const handleChange = (e) => {
        const { name, value } = e.target;
        const error = validateField(name, value);
        
        setValidationErrors({
            ...validationErrors,
            [name]: error
        });
        
        setFormData({
            ...formData,
            [name]: value
        });
    };

    const handleFileChange = (e) => {
        const file = e.target.files[0];
        if (file) {
            setFormData({
                ...formData,
                profilePicture: file
            });
            
            const reader = new FileReader();
            reader.onloadend = () => {
                setPreviewImage(reader.result);
            };
            reader.readAsDataURL(file);
        }
    };

    // Editor interaction helpers (same behavior as desktop)
    const EDITOR_SIZE = 200;
    const PAN_DAMPING = 1.6;
    const PINCH_EXPONENT = 0.65;
    const WHEEL_STEP_IN = 1.02;
    const WHEEL_STEP_OUT = 0.98;

    const clamp = (val, min, max) => Math.max(min, Math.min(max, val));
    const clampPosition = (pos) => ({ x: clamp(pos.x, 0, 1), y: clamp(pos.y, 0, 1) });

    const distanceBetween = (t1, t2) => {
        const dx = t2.clientX - t1.clientX;
        const dy = t2.clientY - t1.clientY;
        return Math.hypot(dx, dy);
    };

    const midpoint = (t1, t2) => ({
        x: (t1.clientX + t2.clientX) / 2,
        y: (t1.clientY + t2.clientY) / 2
    });

    const handleWheel = (e) => {
        const delta = -e.deltaY;
        const factor = delta > 0 ? WHEEL_STEP_IN : WHEEL_STEP_OUT;
        setScale(prev => clamp(prev * factor, 1, 3));
    };

    const handleTouchStart = (e) => {
        const state = gestureStateRef.current;
        const container = editorContainerRef.current;
        if (!container) return;
        if (e.touches.length === 1) {
            state.isPanning = true;
            state.lastPoint = { x: e.touches[0].clientX, y: e.touches[0].clientY };
            state.startPosition = position;
        } else if (e.touches.length === 2) {
            state.isPanning = true;
            state.pinchStartDistance = distanceBetween(e.touches[0], e.touches[1]);
            state.startScale = scale;
            state.startPosition = position;
            state.lastPoint = midpoint(e.touches[0], e.touches[1]);
        }
    };

    const handleTouchMove = (e) => {
        const state = gestureStateRef.current;
        const container = editorContainerRef.current;
        if (!container) return;

        if (e.touches.length === 1 && state.isPanning && state.lastPoint) {
            const current = { x: e.touches[0].clientX, y: e.touches[0].clientY };
            const dx = current.x - state.lastPoint.x;
            const dy = current.y - state.lastPoint.y;
            setPosition(prev => clampPosition({
                x: prev.x - dx / (EDITOR_SIZE * PAN_DAMPING),
                y: prev.y - dy / (EDITOR_SIZE * PAN_DAMPING)
            }));
            state.lastPoint = current;
        }

        if (e.touches.length === 2) {
            const newDist = distanceBetween(e.touches[0], e.touches[1]);
            if (state.pinchStartDistance) {
                const ratio = newDist / state.pinchStartDistance;
                const softened = Math.pow(ratio, PINCH_EXPONENT);
                setScale(clamp(state.startScale * softened, 1, 3));
            }
            const mid = midpoint(e.touches[0], e.touches[1]);
            const dx = mid.x - state.lastPoint.x;
            const dy = mid.y - state.lastPoint.y;
            setPosition(prev => clampPosition({
                x: prev.x - dx / (EDITOR_SIZE * PAN_DAMPING),
                y: prev.y - dy / (EDITOR_SIZE * PAN_DAMPING)
            }));
            state.lastPoint = mid;
        }
    };

    const handleTouchEnd = () => {
        gestureStateRef.current = {
            isPanning: false,
            lastPoint: null,
            pinchStartDistance: null,
            startScale: 1,
            startPosition: position
        };
    };

    const startEditing = (field) => {
        setEditingField(field);
        setValidationErrors({});
        setFormData({
            ...formData,
            firstName: displayData.firstName || '',
            lastName: displayData.lastName || '',
            profileName: displayData.firstName || '',
            email: displayData.email || '',
            phoneNumber: displayData.phoneNumber || '',
            birthDate: toHtmlDateInputValue(displayData.birthDate) || ''
        });
    };

    const openModal = (type) => {
        setModalType(type);
        setFormData({
            ...formData,
            firstName: displayData.firstName || '',
            lastName: displayData.lastName || '',
            profileName: displayData.firstName || '',
            email: displayData.email || '',
            phoneNumber: displayData.phoneNumber || '',
            birthDate: toHtmlDateInputValue(displayData.birthDate) || ''
        });
        setOpen(true);
    };

    const closeModal = () => {
        setOpen(false);
        setPreviewImage(null);
        setOptimisticProfilePicture(null);
        setFormData({
            ...formData,
            profilePicture: null
        });
    };

    const cancelEditing = () => {
        setEditingField(null);
    };

    // Delete account handlers
    const handleOptionSelect = (optionValue) => {
        setSelectedOption(optionValue);
    }

    const handleDeleteTextareaChange = (e) => {
        if (e.target.value.length <= 300) {
            setTextareaContent(e.target.value);
        }
    }

    const getReasonText = () => {
        switch (selectedOption) {
            case "not-renting":
                return "Ich habe kein Bedarf mehr.";
            case "more-bookings":
                return "stu ist nicht die passende Option für mich.";
            case "not-suitable":
                return "Ich war mit der Nutzung nicht zufrieden.";
            case "other":
                return "Sonstiges.";
            default:
                return "Keine Angabe";
        }
    };

    const handleDeleteClick = () => {
        setDisplayDeleteContent(true);
    }

    const closeDeleteContent = () => {
        setDisplayDeleteContent(false);
        setDeleteModalOpen(false);
        setDeleteModalTab(1);
        setSelectedOption("");
        setTextareaContent("");
    }

    const handleDeleteAccount = async () => {
        if (!userId || !selectedOption) return;

        try {
            await dispatch(
                deleteAccount({
                    userId,
                    reason: getReasonText(),
                    feedback: textareaContent || null,
                })
            ).unwrap();

            // After successful deletion, show success tab
            setDeleteModalTab(2);
        } catch (error) {
            console.error("Account deletion failed:", error);
            // Error is handled by Redux state (deleteStatus === "failed")
            // The error message will be displayed in the UI
        }
    };

    const getDeleteErrorMessage = () => {
        if (!deleteError) return null;
        
        // Check if error is an object with code and message
        const errorCode = typeof deleteError === 'object' ? deleteError?.code : null;
        const errorMessage = typeof deleteError === 'object' ? deleteError?.message : deleteError;
        
        // Map error codes to user-friendly German messages
        if (errorCode === 'ACTIVE_BOOKINGS_GUEST') {
            return 'Du hast noch aktive Buchungen. Bitte storniere oder schließe diese zuerst ab.';
        }
        if (errorCode === 'ACTIVE_BOOKINGS_HOST') {
            return 'Deine Studios haben noch aktive Buchungen. Bitte schließe diese zuerst ab.';
        }
        
        // Fallback to the message from backend or generic error
        return errorMessage || 'Entschuldigung, dein Profil konnte momentan nicht gelöscht werden. Bitte versuche es später erneut.';
    };

    const handleDeleteFinish = () => {
        setDeleteModalOpen(false);
        setDisplayDeleteContent(false);
        navigate('/');
    };

    const handleSave = () => {
        // Check for validation errors before saving
        const hasErrors = Object.values(validationErrors).some(error => error !== '');
        if (hasErrors) {
            return;
        }
        
        let updateData = {};
        
        switch (editingField) {
            case 'name':
                // Validate both fields
                const firstNameError = validateField('firstName', formData.firstName);
                const lastNameError = validateField('lastName', formData.lastName);
                if (firstNameError || lastNameError) {
                    setValidationErrors({
                        firstName: firstNameError,
                        lastName: lastNameError
                    });
                    return;
                }
                updateData = {
                    firstName: formData.firstName,
                    lastName: formData.lastName
                };
                break;
            case 'profileName':
                const profileNameError = validateField('profileName', formData.profileName);
                if (profileNameError) {
                    setValidationErrors({ profileName: profileNameError });
                    return;
                }
                updateData = {
                    firstName: formData.profileName
                };
                break;
            case 'birthDate':
                updateData = {
                    birthDate: formData.birthDate
                };
                break;
            case 'phoneNumber':
                const phoneError = validateField('phoneNumber', formData.phoneNumber);
                if (phoneError) {
                    setValidationErrors({ phoneNumber: phoneError });
                    return;
                }
                updateData = {
                    phoneNumber: formData.phoneNumber
                };
                break;
            default:
                break;
        }
        
        // Optimistically update displayData immediately to prevent white flash
        setDisplayData(prev => ({
            ...prev,
            ...updateData
        }));
        
        dispatch(updateUserProfile(updateData))
            .then(() => {
                setEditingField(null);
                setValidationErrors({});
                showNotification();
            })
            .catch(err => {
                console.error('Error updating profile:', err);
                // Revert optimistic update on error
                setDisplayData({
                    profilePicture: profilePicture,
                    firstName: firstName,
                    lastName: lastName,
                    email: email,
                    phoneNumber: phoneNumber,
                    birthDate: birthDate
                });
            });
    };

    const handleModalSave = () => {
        if (modalType === 'profilePicture') {
            if (!formData.profilePicture) return;
            if (!isAllowedProfileImageFile(formData.profilePicture)) {
                notifyValidationToast('Nur JPG, PNG und JPEG sind erlaubt');
                return;
            }

            if (editorRef.current) {
                const canvas = editorRef.current.getImageScaledToCanvas();
                try {
                    const dataUrl = canvas.toDataURL('image/jpeg', 0.92);
                    setOptimisticProfilePicture(dataUrl);
                } catch (e) {}

                canvas.toBlob((blob) => {
                    if (!blob) {
                        console.error('Failed to create image blob');
                        return;
                    }
                    const editedFile = new File(
                        [blob],
                        (formData.profilePicture?.name?.split('.').slice(0, -1).join('.') || 'profile') + '.jpg',
                        { type: 'image/jpeg' }
                    );
                    const updateData = { profilePicture: editedFile };
                    dispatch(updateUserProfile(updateData))
                        .then(() => {
                            closeModal();
                            setOptimisticProfilePicture(null);
                            showNotification();
                        })
                        .catch(err => {
                            setOptimisticProfilePicture(null);
                        });
                }, 'image/jpeg', 0.92);
                return;
            }

            const updateData = { profilePicture: formData.profilePicture };
            setOptimisticProfilePicture(previewImage);
            dispatch(updateUserProfile(updateData))
                .then(() => {
                    closeModal();
                    setOptimisticProfilePicture(null);
                    showNotification();
                })
                .catch(err => {
                    notifySaveError(err, { title: 'Profil konnte nicht aktualisiert werden' });
                    setOptimisticProfilePicture(null);
                });
            return;
        }
    };

    const renderField = (fieldType, label, value, inputType = 'text') => {
        const isEditing = editingField === fieldType;
        
        return (
            <div className={classes.itemsContainer}>
                <div className={classes.nameContainer}>
                    <h3 className="semiboldSmall">{label}</h3>
                    {isEditing ? (
                        fieldType === 'name' ? (
                            <div className={classes.nameInputs}>
                                <div style={{ width: '100%' }}>
                                    <input 
                                        className={`${classes.editInput} regularMiddle ${validationErrors.firstName ? classes.inputError : ''}`}
                                        type="text"
                                        name="firstName"
                                        placeholder="Vorname"
                                        value={formData.firstName}
                                        onChange={handleChange}
                                        autoFocus
                                    />
                                    {validationErrors.firstName && (
                                        <div className={classes.errorContainer}>
                                            <svg xmlns="http://www.w3.org/2000/svg" width="12" height="12" viewBox="0 0 12 12" fill="none">
                                                <path d="M1.02734 1.40723L10.9725 11.3524" stroke="#CB1541" strokeWidth="1.5" strokeMiterlimit="10"/>
                                                <path fillRule="evenodd" clipRule="evenodd" d="M4.9452 6.37988L0.5 1.93469L1.5548 0.879883L6 5.32508L10.4452 0.879883L11.5 1.93469L7.0548 6.37988L11.5 10.8251L10.4452 11.8799L6 7.43469L1.5548 11.8799L0.5 10.8251L4.9452 6.37988Z" fill="#CB1541"/>
                                            </svg>
                                            <span className={classes.errorText}>{validationErrors.firstName}</span>
                                        </div>
                                    )}
                                </div>
                                <div style={{ width: '100%' }}>
                                    <input 
                                        className={`${classes.editInput} regularMiddle ${validationErrors.lastName ? classes.inputError : ''}`}
                                        type="text"
                                        name="lastName"
                                        placeholder="Nachname"
                                        value={formData.lastName}
                                        onChange={handleChange}
                                    />
                                    {validationErrors.lastName && (
                                        <div className={classes.errorContainer}>
                                            <svg xmlns="http://www.w3.org/2000/svg" width="12" height="12" viewBox="0 0 12 12" fill="none">
                                                <path d="M1.02734 1.40723L10.9725 11.3524" stroke="#CB1541" strokeWidth="1.5" strokeMiterlimit="10"/>
                                                <path fillRule="evenodd" clipRule="evenodd" d="M4.9452 6.37988L0.5 1.93469L1.5548 0.879883L6 5.32508L10.4452 0.879883L11.5 1.93469L7.0548 6.37988L11.5 10.8251L10.4452 11.8799L6 7.43469L1.5548 11.8799L0.5 10.8251L4.9452 6.37988Z" fill="#CB1541"/>
                                            </svg>
                                            <span className={classes.errorText}>{validationErrors.lastName}</span>
                                        </div>
                                    )}
                                </div>
                            </div>
                        ) : (
                            <div style={{ width: '100%' }}>
                                <input 
                                    className={`${classes.editInput} regularMiddle ${validationErrors[fieldType] ? classes.inputError : ''}`}
                                    type={inputType}
                                    name={fieldType}
                                    value={formData[fieldType]}
                                    onChange={handleChange}
                                    autoFocus
                                />
                                {validationErrors[fieldType] && (
                                    <div className={classes.errorContainer}>
                                        <svg xmlns="http://www.w3.org/2000/svg" width="12" height="12" viewBox="0 0 12 12" fill="none">
                                            <path d="M1.02734 1.40723L10.9725 11.3524" stroke="#CB1541" strokeWidth="1.5" strokeMiterlimit="10"/>
                                            <path fillRule="evenodd" clipRule="evenodd" d="M4.9452 6.37988L0.5 1.93469L1.5548 0.879883L6 5.32508L10.4452 0.879883L11.5 1.93469L7.0548 6.37988L11.5 10.8251L10.4452 11.8799L6 7.43469L1.5548 11.8799L0.5 10.8251L4.9452 6.37988Z" fill="#CB1541"/>
                                        </svg>
                                        <span className={classes.errorText}>{validationErrors[fieldType]}</span>
                                    </div>
                                )}
                            </div>
                        )
                    ) : (
                        <p className="regularMiddle">{value}</p>
                    )}
                </div>
                {isEditing ? (
                    <div className={classes.editButtons}>
                        <button 
                            onClick={handleSave} 
                            className={`${classes.saveButton} regularSmall`}
                            disabled={status === 'loading' || Object.values(validationErrors).some(error => error !== '')}
                        >
                            {status === 'loading' ? 'Speichern...' : 'Speichern'}
                        </button>
                        <button 
                            onClick={cancelEditing} 
                            className={`${classes.cancelButton} regularSmall`}
                        >
                            Abbrechen
                        </button>
                    </div>
                ) : (
                    <button 
                        onClick={() => startEditing(fieldType)} 
                        className={`${classes.editButton} regularSmall`}
                    >
                        Bearbeiten
                    </button>
                )}
            </div>
        );
    };

    const renderModalContent = () => {
        switch (modalType) {
            case 'profilePicture':
                return (
                    <div className={classes.modalContainer}>
                        <header className={classes.modalHeader}>
                            <h2 className='mediumLarge'>Profilbild ändern</h2>
                            <button onClick={closeModal}>
                                <svg xmlns="http://www.w3.org/2000/svg" width="17" height="17" viewBox="0 0 17 17" fill="none">
                                    <path fillRule="evenodd" clipRule="evenodd" d="M7.50947 8.5L0 0.990535L0.990535 0L8.5 7.50947L16.0095 0L17 0.990535L9.49054 8.5L17 16.0095L16.0095 17L8.5 9.49054L0.990535 17L0 16.0095L7.50947 8.5Z" fill="#1F1F1F"/>
                                </svg>
                            </button>
                        </header>
                        <div className={classes.modalContent}>
                            {!previewImage && (
                                <div className={classes.editInputContainer}>
                                    <label className='semiboldSmall' htmlFor="profilePicture">Profilbild</label>
                                    <input 
                                        className={`${classes.fileInput} slimRegularLarge`} 
                                        type="file" 
                                        id="profilePicture"
                                        name="profilePicture"
                                        accept="image/*"
                                        onChange={handleFileChange}
                                    />
                                </div>
                            )}
                            {previewImage && (
                                <div className={classes.previewContainer}>
                                    <div
                                        className={classes.previewImageContainer}
                                        ref={editorContainerRef}
                                        onWheel={handleWheel}
                                        onTouchStart={handleTouchStart}
                                        onTouchMove={handleTouchMove}
                                        onTouchEnd={handleTouchEnd}
                                        onTouchCancel={handleTouchEnd}
                                    >
                                        <AvatarEditor
                                            ref={editorRef}
                                            image={previewImage}
                                            width={200}
                                            height={200}
                                            border={0}
                                            borderRadius={200}
                                            color={[255, 255, 255, 0]}
                                            scale={scale}
                                            rotate={0}
                                            position={position}
                                            onPositionChange={setPosition}
                                            style={{ width: '200px', height: '200px', borderRadius: '50%' }}
                                        />
                                    </div>
                                </div>
                            )}
                        </div>
                        <footer className={classes.modalFooter}>
                            <button onClick={closeModal} className={`${classes.cancelButton} mediumLarge`}>Abbrechen</button>
                            <button 
                                onClick={handleModalSave} 
                                className={`${classes.saveButton} mediumLarge`}
                                disabled={status === 'loading' || !formData.profilePicture}
                            >
                                {status === 'loading' ? 'Speichern...' : 'Speichern'}
                            </button>
                        </footer>
                    </div>
                );
            default:
                return null;
        }
    };

    const isInfoLoading = !displayData.email && !displayData.firstName;

    return (
        <>
        <div className={classes.personalDataContainer}>
            <div className={classes.generalContainer}>
                {isInfoLoading ? (
                    <div className={classes.itemsContainer}>
                        <div className={classes.nameContainer}>
                            <div className={classes.skeletonLabel}></div>
                        </div>
                        <div className={classes.skeletonAvatar}></div>
                    </div>
                ) : (
                    <div className={classes.itemsContainer}>
                        <h3 className="semiboldSmall">Profilbild</h3>
                        <div className={classes.profilePicture} onClick={() => openModal('profilePicture')}>
                            <div className={classes.profilePictureOverlay}>
                                <img 
                                    src={optimisticProfilePicture || displayData.profilePicture} 
                                    alt="Profilbild" 
                                    style={{
                                        opacity: status === 'loading' && modalType === 'profilePicture' ? 0.7 : 1,
                                        transition: 'opacity 0.2s ease-in-out'
                                    }}
                                />
                                {status === 'loading' && modalType === 'profilePicture' && (
                                    <div className={classes.uploadingOverlay}>
                                        <div className={classes.spinner}></div>
                                    </div>
                                )}
                            </div>
                            <div className={classes.profilePictureIcon}>
                                <svg xmlns="http://www.w3.org/2000/svg" width="15" height="15" viewBox="0 0 15 15" fill="none">
                                    <g clipPath="url(#clip0_7756_3110)">
                                    <path d="M6.52329 1.86719H2.45275C1.40657 1.86719 0.558594 2.71517 0.558594 3.76134V11.9024C0.558594 12.9486 1.40657 13.7966 2.45275 13.7966H10.5938C11.64 13.7966 12.488 12.9486 12.488 11.9024V7.83188" stroke="black" strokeWidth="0.944826" strokeLinecap="round" strokeLinejoin="round"/>
                                    <path d="M5.38827 6.87149L5.25781 8.96383L7.35141 8.83463M7.35141 8.83463C9.11009 7.07595 10.875 5.31101 12.6337 3.55233M7.35141 8.83463C9.11009 7.07721 10.875 5.31101 12.6337 3.55233M12.6337 3.55233L11.2802 4.90583C11.7531 4.43292 13.5281 2.65919 13.858 2.32929C13.4365 1.62306 12.5183 0.760025 11.8196 0.441406C11.5022 0.758771 11.0783 1.18151 10.5953 1.66571C9.68335 2.65041 11.7218 4.53829 12.6337 3.55233Z" stroke="black" strokeWidth="0.944826" strokeLinecap="round" strokeLinejoin="round"/>
                                    <path d="M5.38672 6.87102L6.21086 6.04688" stroke="black" strokeWidth="0.944826" strokeLinecap="round" strokeLinejoin="round"/>
                                    <path d="M6.21503 6.04821C7.67014 4.59184 7.78806 4.47393 9.24442 3.01882L10.5979 1.66406C9.40248 2.85951 6.0783 6.18368 6.21503 6.04695V6.04821Z" stroke="black" strokeWidth="0.944826" strokeLinecap="round" strokeLinejoin="round"/>
                                    </g>
                                    <defs>
                                    <clipPath id="clip0_7756_3110">
                                    <rect width="14.1748" height="14.2337" fill="white" transform="translate(0.121094)"/>
                                    </clipPath>
                                    </defs>
                                </svg>
                            </div>
                        </div>
                    </div>
                )}
                <div className={classes.horizontalLine}></div>
                
                {isInfoLoading ? (
                    <div className={classes.itemsContainer}>
                        <div className={classes.nameContainer}>
                            <div className={classes.skeletonLabel}></div>
                            <div className={classes.skeletonValue}></div>
                        </div>
                        <div className={classes.skeletonButton}></div>
                    </div>
                ) : (
                    renderField('name', 'Offizieller Name', `${displayData.firstName} ${displayData.lastName}`)
                )}
                <div className={classes.horizontalLine}></div>
                
                {/* {isInfoLoading ? (
                    <div className={classes.itemsContainer}>
                        <div className={classes.nameContainer}>
                            <div className={classes.skeletonLabel}></div>
                            <div className={classes.skeletonValue}></div>
                        </div>
                        <div className={classes.skeletonButton}></div>
                    </div>
                ) : (
                    renderField('profileName', 'Profilname', displayData.firstName)
                )} */}
                {/* <div className={classes.horizontalLine}></div> */}
                
                {isInfoLoading ? (
                    <div className={classes.itemsContainer}>
                        <div className={classes.nameContainer}>
                            <div className={classes.skeletonLabel}></div>
                            <div className={classes.skeletonValue}></div>
                        </div>
                        <div className={classes.skeletonButton}></div>
                    </div>
                ) : (
                    renderField('birthDate', 'Geburtsdatum', formatDate(displayData.birthDate), 'date')
                )}
                {/* <div className={classes.horizontalLine}></div> */}
                
                {/* <div className={classes.itemsContainer}>
                    <div className={classes.nameContainer}>
                        <h3 className="semiboldSmall">Geschlecht</h3>
                        <p className="regularMiddle">Männlich</p>
                    </div>
                    <button className={`${classes.editButton} regularSmall`}>Bearbeiten</button>
                </div> */}
            </div>
            
            <h2 className={`${classes.generalTitle} titleSemibold3`}>Kontaktdaten</h2>
            <div className={classes.generalContainer}>
                {isInfoLoading ? (
                    <div className={classes.itemsContainer}>
                        <div className={classes.nameContainer}>
                            <div className={classes.skeletonLabel}></div>
                            <div className={classes.skeletonValue}></div>
                        </div>
                    </div>
                ) : (
                    <div className={classes.itemsContainer}>
                        <div className={classes.nameContainer}>
                            <h3 className="semiboldSmall">E-Mail</h3>
                            <p className="regularMiddle">{displayData.email}</p>
                        </div>
                    </div>
                )}
                <div className={classes.horizontalLine}></div>
                {isInfoLoading ? (
                    <div className={classes.itemsContainer}>
                        <div className={classes.nameContainer}>
                            <div className={classes.skeletonLabel}></div>
                            <div className={classes.skeletonValue}></div>
                        </div>
                        <div className={classes.skeletonButton}></div>
                    </div>
                ) : (
                    renderField('phoneNumber', 'Telefonnummer', displayData.phoneNumber, 'tel')
                )}
            </div>

            {/* Delete Account Section */}
            <div className={classes.generalContainer} style={{ marginTop: '30px' }}>
                <div className={classes.itemsContainer}>
                    <div className={classes.nameContainer}>
                        <h3 style={{color: 'var(--stu-red-dark)'}} className="semiboldSmall">Profil löschen</h3>
                        <p className="regularMiddle">Profil wird dauerhaft gelöscht.</p>
                    </div>
                    <button onClick={handleDeleteClick} className={`${classes.editButton} regularSmall`}>
                        Löschen
                    </button>
                </div>
            </div>
            
            <SmallModal
                open={open}
                onBackdropClick={closeModal}
                title={`${modalType} bearbeiten`}
                fromDeleteStudio={true}
            >
                {renderModalContent()}
            </SmallModal>
            
            {/* Delete Account Modals */}
            {displayDeleteContent && (
                <div className={accountDetailsClasses.deleteContainer}>
                    <header className={accountDetailsClasses.modalHeaderDeleteProfile}>
                        <button className={accountDetailsClasses.backButtondDeleteModal} onClick={closeDeleteContent}>
                            <svg xmlns="http://www.w3.org/2000/svg" width="11" height="18" viewBox="0 0 11 18" fill="none">
                                <path fillRule="evenodd" clipRule="evenodd" d="M0.853516 8.66022L9.01373 0.5L10.1038 1.59009L2.9916 8.70232L10.1473 16.4543L9.01452 17.5L0.869196 8.6759L0.853516 8.66022Z" fill="#1F1F1F"/>
                            </svg>
                        </button>
                        <h2 className='titleSemibold3'>Profil löschen</h2>
                        <button className={accountDetailsClasses.closeButton} onClick={closeDeleteContent}>
                            <svg xmlns="http://www.w3.org/2000/svg" width="17" height="17" viewBox="0 0 17 17" fill="none">
                                <path fillRule="evenodd" clipRule="evenodd" d="M7.50947 8.5L0 0.990535L0.990535 0L8.5 7.50947L16.0095 0L17 0.990535L9.49054 8.5L17 16.0095L16.0095 17L8.5 9.49054L0.990535 17L0 16.0095L7.50947 8.5Z" fill="#1F1F1F"/>
                            </svg>
                        </button>
                    </header>
                    <div className={accountDetailsClasses.modalContentDeleteProfile}>
                        <div className={accountDetailsClasses.questionaryContainer}>
                            <h3 className="semiboldMiddle">Wieso möchtest du dein Profil löschen?</h3>
                            <div className={accountDetailsClasses.optionsContainer}>
                                <div 
                                    className={`${accountDetailsClasses.option} ${selectedOption === "not-renting" ? accountDetailsClasses.selected : ""}`}
                                    onClick={() => handleOptionSelect("not-renting")}
                                >
                                    <div className={accountDetailsClasses.customCheckbox}>
                                        <div 
                                            className={accountDetailsClasses.checkboxOuter}
                                            style={{borderColor: selectedOption === "not-renting" ? "var(--dark-grey)" : ""}}
                                        >
                                            {selectedOption === "not-renting" && <div className={accountDetailsClasses.checkboxInner}></div>}
                                        </div>
                                    </div>
                                    <span className="regularMiddle">Ich habe kein Bedarf mehr.</span>
                                </div>
                                <div 
                                    className={`${accountDetailsClasses.option} ${selectedOption === "more-bookings" ? accountDetailsClasses.selected : ""}`}
                                    onClick={() => handleOptionSelect("more-bookings")}
                                >
                                    <div className={accountDetailsClasses.customCheckbox}>
                                        <div 
                                            className={accountDetailsClasses.checkboxOuter}
                                            style={{borderColor: selectedOption === "more-bookings" ? "var(--dark-grey)" : ""}}
                                        >
                                            {selectedOption === "more-bookings" && <div className={accountDetailsClasses.checkboxInner}></div>}
                                        </div>
                                    </div>
                                    <span className="regularMiddle">stu ist nicht die passende Option für mich.</span>
                                </div>
                                <div 
                                    className={`${accountDetailsClasses.option} ${selectedOption === "not-suitable" ? accountDetailsClasses.selected : ""}`}
                                    onClick={() => handleOptionSelect("not-suitable")}
                                >
                                    <div className={accountDetailsClasses.customCheckbox}>
                                        <div 
                                            className={accountDetailsClasses.checkboxOuter}
                                            style={{borderColor: selectedOption === "not-suitable" ? "var(--dark-grey)" : ""}}
                                        >
                                            {selectedOption === "not-suitable" && <div className={accountDetailsClasses.checkboxInner}></div>}
                                        </div>
                                    </div>
                                    <span className="regularMiddle">Ich war mit der Nutzung nicht zufrieden.</span>
                                </div>
                                <div 
                                    className={`${accountDetailsClasses.option} ${selectedOption === "other" ? accountDetailsClasses.selected : ""}`}
                                    onClick={() => handleOptionSelect("other")}
                                >
                                    <div className={accountDetailsClasses.customCheckbox}>
                                        <div 
                                            className={accountDetailsClasses.checkboxOuter} 
                                            style={{borderColor: selectedOption === "other" ? "var(--dark-grey)" : ""}}
                                        >
                                            {selectedOption === "other" && <div className={accountDetailsClasses.checkboxInner}></div>}
                                        </div>
                                    </div>
                                    <span className="regularMiddle">Sonstiges.</span>
                                </div>
                            </div>
                        </div>
                        <div className={accountDetailsClasses.textareaContainer}>
                            <div className={accountDetailsClasses.titleContainer}>
                                <h3 className="semiboldMiddle">Wie können wir stu verbessern?</h3>
                                <h3 className={accountDetailsClasses.optionalText}>(Optional)</h3>
                            </div>
                            <textarea
                                className={`${accountDetailsClasses.feedbackTextarea} slimRegularLargeText`}
                                value={textareaContent}
                                onChange={handleDeleteTextareaChange}
                                maxLength={300}
                            />
                            <div className={`${accountDetailsClasses.characterCount} slimRegularSmallText`}>
                                {textareaContent.length}/300
                            </div>
                        </div>
                    </div>
                    <footer className={accountDetailsClasses.modalDeleteProfileFooter}>
                        <button 
                            className={`${accountDetailsClasses.cancelButtonSecond} mediumLarge`}
                            onClick={closeDeleteContent}
                        >
                            Abbrechen
                        </button>
                        <button 
                            className={`${accountDetailsClasses.continueButton} mediumLarge ${!selectedOption ? accountDetailsClasses.disabled : ""}`}
                            disabled={!selectedOption}
                            onClick={() => {
                                setDisplayDeleteContent(false);
                                setDeleteModalOpen(true);
                            }}
                        >
                            Weiter
                        </button>
                    </footer>
                </div>
            )}
            
            <SmallModal
                open={deleteModalOpen}
                onBackdropClick={() => setDeleteModalOpen(false)}
                fromDeleteStudio={true}
            >
                {deleteModalTab === 1 && (
                    <div className={accountDetailsClasses.modalContentSecondDeleteProfile}>
                        <header className={accountDetailsClasses.modalHeaderSecondDeleteProfile}>
                            <h2 className='titleSemibold3'>Profil löschen</h2>
                            <button className={accountDetailsClasses.closeButton} onClick={() => setDeleteModalOpen(false)}>
                                <svg xmlns="http://www.w3.org/2000/svg" width="17" height="17" viewBox="0 0 17 17" fill="none">
                                    <path fillRule="evenodd" clipRule="evenodd" d="M7.50947 8.5L0 0.990535L0.990535 0L8.5 7.50947L16.0095 0L17 0.990535L9.49054 8.5L17 16.0095L16.0095 17L8.5 9.49054L0.990535 17L0 16.0095L7.50947 8.5Z" fill="#1F1F1F"/>
                                </svg>
                            </button>
                        </header>
                        <p className={accountDetailsClasses.deleteProfileText}>
                            Dein Profil & alle Daten <br/>
                            werden dauerhaft gelöscht.</p>
                        {deleteStatus === "failed" && getDeleteErrorMessage() && (
                            <p className={`${accountDetailsClasses.deleteErrorText} mediumLarge`}>
                                {getDeleteErrorMessage()}
                            </p>
                        )}
                        <div className={accountDetailsClasses.deleteProfileButtonContainer}>
                            <button 
                                onClick={handleDeleteAccount} 
                                className={`${accountDetailsClasses.deleteProfileButton} mediumLarge`}
                                disabled={deleteStatus === "loading"}
                            >
                                {deleteStatus === "loading" ? "Bitte warten..." : "Profil löschen"}
                            </button>
                            <button onClick={() => setDeleteModalOpen(false)} className={`${accountDetailsClasses.cancelButton} mediumLarge`}>Abbrechen</button>
                        </div>
                    </div>
                )}
                {deleteModalTab === 2 &&
                    <div className={accountDetailsClasses.successModalTab}>
                        <header className={accountDetailsClasses.successModalHeader}>
                            <div className={accountDetailsClasses.successModalIcon}>
                                <svg xmlns="http://www.w3.org/2000/svg" width="17" height="13" viewBox="0 0 17 13" fill="none">
                                    <path fillRule="evenodd" clipRule="evenodd" d="M16.8237 1.18765L5.13496 12.3054L5.13474 12.3052L5.13364 12.3063L0.175781 7.24179L1.29183 6.14924L5.17296 10.1139L15.7473 0.0559998L16.8237 1.18765Z" fill="white"/>
                                </svg>
                            </div>
                            <h2 className="titleSemibold3">Dein Profil wurde gelöscht.</h2>
                        </header>
                        <p className={`${accountDetailsClasses.successText} regularSmall`}>
                            Danke für dein Vertrauen in unser Produkt.
                            <br/>
                            Wir hoffen, dich weiterhin begleiten.
                        </p>
                        <button 
                            onClick={handleDeleteFinish} 
                            className={`${accountDetailsClasses.navigateButton} mediumLarge`}
                        >
                            Fertig
                        </button>
                    </div>
                }
            </SmallModal>
            <SaveNotification
                show={showSaveNotification}
                onHide={() => setShowSaveNotification(false)}
                message={saveNotificationMessage}
            />
        </div>
        </>
    );
};

export default PersonalData;
